// NEW_FILE_JUSTIFIED: Custom hook for AI notes CRUD — extracts query + mutation + editing state from AiNotesSection
import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated types live outside src/
import type { TAiReplyPlatform } from '@/@types/aiReply';

export function useAiNotes(contactIdentifier: string, platform: TAiReplyPlatform) {
  const notes = useQuery(
    api['aiReply/contactNotes'].listForContact,
    { contactIdentifier, platform }
  );
  const updateMutation = useMutation(api['aiReply/contactNotes'].update);
  const removeMutation = useMutation(api['aiReply/contactNotes'].remove);

  const [editingNoteId, setEditingNoteId] = useState<Id<'contactNotes'> | null>(null);
  const [editContent, setEditContent] = useState('');

  function startEditing(noteId: Id<'contactNotes'>, content: string) {
    setEditingNoteId(noteId);
    setEditContent(content);
  }

  function cancelEditing() {
    setEditingNoteId(null);
    setEditContent('');
  }

  async function saveEdit() {
    if (editingNoteId === null) return;
    try {
      await updateMutation({ noteId: editingNoteId, content: editContent });
      toast.success('Note updated');
      cancelEditing();
    } catch {
      toast.error('Failed to update note');
    }
  }

  async function deleteNote(noteId: Id<'contactNotes'>) {
    try {
      await removeMutation({ noteId });
      toast.success('Note deleted');
    } catch {
      toast.error('Failed to delete note');
    }
  }

  return {
    notes,
    editingNoteId,
    editContent,
    setEditContent,
    startEditing,
    cancelEditing,
    saveEdit,
    deleteNote,
  };
}
